/*
 * Collection pages, MAIN 7.x track E (2026-09-28):
 *  - <jk-design-cards>: design/drop cards that filter the grid.
 *      server mode: the card links toggle the Search & Discovery Design filter
 *                   (rendered through FacetFiltersForm, like any other filter).
 *      client mode: no Design filter yet; loads every page of the grid, then
 *                   hides the other designs. URL keeps ?design=<key>.
 *  - filter table: remembers which groups a shopper opened/closed, keeps the
 *    group "(n)" counts and the FILTER button count in step.
 */
(() => {
  const DESIGN = 'design';
  const hasFacets = () => typeof FacetFiltersForm !== 'undefined';
  const grid = () => document.getElementById('product-grid');

  function paramsWithout(search, keys) {
    const p = new URLSearchParams(search);
    keys.forEach((k) => p.delete(k));
    return p;
  }

  function productWord(n) {
    return n + (n === 1 ? ' product' : ' products');
  }

  class JkDesignCards extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode;
      this.cards = Array.from(this.querySelectorAll('.jk-dc__card'));
      this.clear = this.querySelector('.jk-dc__clear');
      this.cards.forEach((card) => card.addEventListener('click', (e) => this.onCard(e, card)));
      if (this.clear) this.clear.addEventListener('click', (e) => this.onClear(e));

      if (this.mode === 'server') {
        this.thumbCache();
      } else {
        const key = new URLSearchParams(location.search).get(DESIGN);
        this.apply(key || null);
      }
    }

    /* ---------- server mode ---------- */
    thumbCache() {
      const base = 'jkthumb:' + this.dataset.collection + ':';
      this.cards.forEach((card) => {
        const img = card.querySelector('img');
        try {
          if (img) sessionStorage.setItem(base + card.dataset.key, img.getAttribute('src'));
          else {
            const src = sessionStorage.getItem(base + card.dataset.key);
            if (src) card.querySelector('.jk-dc__thumb').innerHTML = `<img src="${src}" alt="" width="80" height="80" loading="lazy" decoding="async">`;
          }
        } catch (e) { /* storage blocked: text-only card */ }
      });
    }

    renderServer(href) {
      const i = href.indexOf('?');
      const qs = paramsWithout(i === -1 ? '' : href.slice(i + 1), ['page']).toString();
      if (hasFacets()) {
        FacetFiltersForm.renderPage(qs);
      } else {
        location.href = location.pathname + (qs ? '?' + qs : '');
      }
    }

    /* ---------- client mode ---------- */
    async loadAll() {
      const g = grid();
      if (!g || g.dataset.jkAll === '1') return;
      const pages = parseInt(g.dataset.jkPages || '1', 10);
      if (pages > 1) {
        const params = paramsWithout(location.search, ['page', DESIGN, 'section_id']);
        const section = this.dataset.section;
        const seen = new Set(Array.from(g.querySelectorAll(':scope > li[data-jk-handle]')).map((li) => li.dataset.jkHandle));
        const urls = [];
        for (let n = 2; n <= pages; n++) {
          const p = new URLSearchParams(params);
          p.set('section_id', section);
          p.set('page', n);
          urls.push(location.pathname + '?' + p.toString());
        }
        g.classList.add('loading');
        const htmls = await Promise.all(urls.map((u) => fetch(u).then((r) => r.text()).catch(() => '')));
        g.classList.remove('loading');
        htmls.forEach((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const src = doc.getElementById('product-grid');
          if (!src) return;
          src.querySelectorAll(':scope > li[data-jk-handle]').forEach((li) => {
            if (seen.has(li.dataset.jkHandle)) return;
            seen.add(li.dataset.jkHandle);
            g.appendChild(document.importNode(li, true));
          });
        });
        if (window.lazySizes) g.querySelectorAll('.lazyload').forEach((img) => lazySizes.loader.unveil(img));
        // Everything is on the page now: retire the infinite-scroll / load-more control.
        const pag = document.querySelector(`pagination-theme[data-section="${section}"]`);
        if (pag) {
          if (pag.observer) pag.observer.disconnect();
          pag.remove();
        }
      }
      g.dataset.jkAll = '1';
    }

    async apply(key) {
      const g = grid();
      this.active = key;
      this.cards.forEach((card) => {
        const on = card.dataset.key === key;
        card.classList.toggle('is-active', on);
        card.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (this.clear) this.clear.hidden = !key;
      this.syncHiddenInput(key);
      if (!g) return;
      if (key) {
        await this.loadAll();
        if (this.active !== key) return; // a newer tap won
      }
      let shown = 0;
      g.querySelectorAll(':scope > li').forEach((li) => {
        const hide = !!key && li.dataset.jkDesign !== key;
        li.classList.toggle('jk-dc-hidden', hide);
        if (!hide) shown++;
      });
      this.updateCount(key ? shown : null);
      document.documentElement.classList.remove('jk-dpending');
    }

    // The sort control in the bar submits its own form: carry ?design= through it.
    syncHiddenInput(key) {
      const form = document.getElementById('FacetFiltersForm-bar');
      if (!form) return;
      let input = form.querySelector('input[name="design"]');
      if (!key) { if (input) input.remove(); return; }
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = DESIGN;
        form.appendChild(input);
      }
      input.value = key;
    }

    updateCount(n) {
      document.querySelectorAll('#ProductCount .facets__label').forEach((el) => {
        if (el.dataset.jkOrig === undefined) el.dataset.jkOrig = el.textContent.trim();
        const t = n === null ? el.dataset.jkOrig : productWord(n);
        if (el.textContent.trim() !== t) el.textContent = t;
      });
    }

    pushUrl(key) {
      const p = paramsWithout(location.search, [DESIGN, 'page']);
      if (key) p.set(DESIGN, key);
      const qs = p.toString();
      history.pushState({ searchParams: qs }, '', location.pathname + (qs ? '?' + qs : ''));
      if (hasFacets()) FacetFiltersForm.searchParamsPrev = qs;
    }

    /* ---------- events ---------- */
    onCard(e, card) {
      e.preventDefault();
      if (this.mode === 'server') {
        this.renderServer(card.getAttribute('href'));
        return;
      }
      const key = card.dataset.key === this.active ? null : card.dataset.key;
      this.pushUrl(key);
      this.apply(key);
    }

    onClear(e) {
      e.preventDefault();
      if (this.mode === 'server') {
        this.renderServer(this.clear.getAttribute('href'));
        return;
      }
      this.pushUrl(null);
      this.apply(null);
    }
  }
  if (!customElements.get('jk-design-cards')) customElements.define('jk-design-cards', JkDesignCards);

  // Back/forward without facets.js (it normally re-renders the grid on popstate).
  window.addEventListener('popstate', () => {
    if (hasFacets()) return;
    const el = document.querySelector('jk-design-cards[data-mode="client"]');
    if (el) el.apply(new URLSearchParams(location.search).get(DESIGN));
  });

  /* ---------- filter table state ---------- */
  const STORE = 'jkft:open';
  const readOpen = () => { try { return JSON.parse(sessionStorage.getItem(STORE) || '{}'); } catch (e) { return {}; } };
  const writeOpen = (m) => { try { sessionStorage.setItem(STORE, JSON.stringify(m)); } catch (e) {} };
  const groupKey = (details) => {
    const ctx = details.closest('.jk-ft--mobile') ? 'm' : 'd';
    const g = details.closest('[data-group]');
    return g ? ctx + ':' + g.dataset.group : null;
  };

  // Record what the shopper opens or closes; re-apply after every re-render.
  document.addEventListener('toggle', (e) => {
    const d = e.target;
    if (!(d instanceof HTMLDetailsElement) || !d.classList.contains('jk-ft__details')) return;
    if (d.dataset.jkApplying) return;
    const k = groupKey(d);
    if (!k) return;
    const m = readOpen();
    m[k] = d.open;
    writeOpen(m);
  }, true);

  function restoreOpen(root) {
    const m = readOpen();
    root.querySelectorAll('.jk-ft__details').forEach((d) => {
      const k = groupKey(d);
      if (!k || !(k in m) || d.open === m[k]) return;
      d.dataset.jkApplying = '1';
      d.open = m[k];
      setTimeout(() => delete d.dataset.jkApplying, 0);
    });
  }

  function syncGroupCounts(root) {
    root.querySelectorAll('.jk-ft__group[data-group]').forEach((g) => {
      const n = g.querySelectorAll('input[type="checkbox"]:checked').length;
      const label = g.querySelector('.jk-ft__label');
      if (!label) return;
      let span = label.querySelector('.jk-ft__n');
      if (!n) { if (span) span.remove(); return; }
      if (!span) { span = document.createElement('span'); span.className = 'jk-ft__n'; label.append(' ', span); }
      const t = '(' + n + ')';
      if (span.textContent !== t) span.textContent = t;
    });
    root.querySelectorAll('.jk-ft__opt').forEach((li) => {
      const input = li.querySelector('input');
      if (input) li.classList.toggle('is-on', input.checked);
    });
  }

  // FILTER button: show how many filters are on, e.g. "Filter (2)".
  function syncToggleCount() {
    const toggle = document.querySelector('.jk-abba #Facets-Toggle');
    if (!toggle) return;
    const n = document.querySelectorAll('.jk-abba .active-facets .active-facets__button').length;
    let span = toggle.querySelector('.jk-toggle-n');
    if (!span) { span = document.createElement('span'); span.className = 'jk-toggle-n'; toggle.appendChild(span); }
    const t = n ? ' (' + n + ')' : '';
    if (span.textContent !== t) span.textContent = t;
  }

  // Garment chips read "zip-hoodie" (the product type handle): show "zip hoodie".
  function tidyChips() {
    document.querySelectorAll('.jk-abba .active-facets__button').forEach((a) => {
      const t = a.firstChild;
      if (t && t.nodeType === 3 && /^\s*[a-z]+(-[a-z]+)+\s*$/.test(t.textContent)) {
        const v = t.textContent.replace(/-/g, ' ');
        if (v !== t.textContent) t.textContent = v;
      }
    });
  }

  function refresh() {
    tidyChips();
    document.querySelectorAll('.jk-ft').forEach((root) => { restoreOpen(root); syncGroupCounts(root); });
    syncToggleCount();
  }

  document.addEventListener('change', (e) => {
    const ft = e.target.closest && e.target.closest('.jk-ft');
    if (ft) syncGroupCounts(ft);
  });

  function watch() {
    refresh();
    const targets = [document.getElementById('ProductGridContainer'), document.getElementById('Facet-Drawer')].filter(Boolean);
    const mo = new MutationObserver(() => { clearTimeout(watch.t); watch.t = setTimeout(refresh, 30); });
    targets.forEach((t) => mo.observe(t, { childList: true, subtree: true }));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
  else watch();
})();

/*
 * <jk-cd-cover>: the album header's disc (track E2, 2026-09-29; markup in
 * sections/jk-collection-cd.liquid, styles in jk-collection.css).
 *  - The disc art loads only after the case photo (or after 3 s). While the case slides
 *    in (round 3, the section's inline script), the disc stays tucked; then it peeks.
 *  - Mouse / trackpad: the disc is pulled toward the pointer wherever it is on the
 *    page, also while the page scrolls (recomputed from the pointer's last position).
 *    It stays tethered to the case: up to half the case width out of the open (right)
 *    side, never out of the hinge side (a pointer on the left tucks it in), less toward
 *    the screen edge so it never leaves the screen. When the pointer leaves the window it
 *    settles back to rest. 7.12 (2026-10-01, Dan: "the CD should only operate horizontally
 *    now, no vertical adjustment"): the pull is sideways only; the pointer's height is
 *    ignored and the disc never moves up or down (it was a tenth of the case width).
 *  - Touch, or before the pointer has moved: the disc rests half out and tucks back
 *    into the case as the header scrolls away.
 *  - The case is a button: press it (click, tap, Enter or Space) and the disc comes
 *    fully out and spins; press again to put it back.
 *  - Unwrapped case (7.12, 2026-10-01, Dan: "like the listening station"): when the
 *    collection has custom.cd_case_open, taking the disc out sweeps the shrink-wrap off
 *    (the unwrapped photo underneath, a soft-edged mask sweeping the wrapped one away, the
 *    hub's grammar) and the disc follows 0.25 s later; putting it back brings the wrap
 *    back. The unwrapped photo is not loaded with the page: the first hover, focus or press
 *    of the case starts it. Not loaded yet at the press: the disc comes out at once and the
 *    wrap comes off when the photo arrives (if the disc is still out).
 *  - Motion is a critically damped spring (weight, no overshoot) with a slight roll
 *    and a tiny counter-nudge of the case. One requestAnimationFrame loop, running
 *    only while the header is on screen and something is still moving.
 *  - Reduced motion: none of the above runs (CSS holds the disc half out, still).
 *  - 7.18 (Dan, 2026-10-09: "remove wrapper state completely on mobile collection pages for the cd, having it
 *    always be active and spinning instead"): below 768px the disc is always fully out and spinning, the case shows
 *    the unwrapped photo (the case <picture>'s phone <source>) and is not a button (no role, no tab stop, a tap does
 *    nothing). Crossing 768px (a phone turned sideways) switches between the two behaviours.
 *  - 7.18 (Dan, 2026-10-09: "searching and selecting a different character should ... pack up your cd into your cd
 *    cover, and slide it out"): leave(go) is the pack-up the search bar (sections/jk-collection-finder.liquid) runs:
 *    the disc goes back in (a stiffer spring), the wrap sweeps back on if the case was unwrapped, then the case slides
 *    off by the nearer screen edge with the hub's tilt and glide, and go() is called just before it is gone. It
 *    returns the side it leaves by (-1 left, 1 right) for the hand-off note, or 0 when the case is (mostly) off screen
 *    and only the fade runs. prepareLeave() (first use of the search) fetches what a phone needs to re-wrap: the
 *    wrapped photo, and the unwrapped photo as its own layer. Back (pageshow from the back-forward cache) undoes it.
 * <jk-tracklist>: the header's tracklist as the design picker (768px up; the design
 *   cards are hidden there but stay in the page). A track press = a press on the matching
 *   card, so filtering, ?design= links and Back stay the cards' job; the tracks mirror
 *   the cards' pressed state, and SHOW ALL presses the cards' Clear.
 */
(() => {
  if (customElements.get('jk-cd-cover')) return;
  const RM = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Phones and tablets without a mouse or trackpad stay on the scroll behaviour.
  const FINE = window.matchMedia('(any-pointer: fine)');
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const OMEGA = 9; // spring stiffness (rad/s): settles in about half a second
  // 7.18: phones keep the disc out and the case unwrapped (keep in step with the 767px rules in jk-collection.css)
  const PHONE = window.matchMedia('(max-width: 767px)');
  const EXIT_EASE = 'cubic-bezier(.3, .05, .45, 1)'; // the hub's CD exit glide (sections/hub-opening.liquid)

  class JkCdCover extends HTMLElement {
    connectedCallback() {
      this.cd = this.querySelector('.jk-cd');
      this.disc = this.querySelector('.jk-cd__disc');
      this.caseEl = this.querySelector('.jk-cd__case');
      this.openImg = this.querySelector('.jk-cd__open');
      this.wrapImg = this.querySelector('img.jk-cd__wrap');
      this.phoneSrc = this.querySelector('.jk-cd__case picture source');
      if (!this.cd || !this.disc || !this.caseEl) return;
      this.loadDisc();
      if (RM.matches) return;
      this.live = true;
      // While the case waits for its picture or slides in (round 3), the disc stays
      // tucked; it peeks out once the case has landed (or was shown at rest).
      this.hold = this.classList.contains('is-armed') || this.classList.contains('is-sliding');
      if (this.hold) {
        const release = () => { if (!this.hold) return; this.hold = false; this.classList.remove('is-sliding'); this.kick(); };
        this.addEventListener('animationend', (e) => { if (e.target === this.cd) release(); });
        this.addEventListener('jk-rest', release, { once: true });
        if (this.classList.contains('is-sliding') && this.cd.getAnimations) {
          const anims = this.cd.getAnimations();
          if (!anims.length) release();
          else Promise.all(anims.map((a) => a.finished)).then(release, release);
        }
        setTimeout(release, 4000);
      }
      this.x = 0; this.y = 0; this.vx = 0; this.vy = 0; this.tx = 0; this.ty = 0;
      this.px = null; this.py = null; this.out = false;
      this.raf = 0; this.visible = false;
      this.tick = this.tick.bind(this);
      this.kick = this.kick.bind(this);
      this.onMove = (e) => {
        if (e.pointerType === 'touch' || !FINE.matches || this.leaving) return;
        this.px = e.clientX; this.py = e.clientY;
        this.kick();
      };
      this.onLeave = (e) => {
        if (e.relatedTarget) return;
        this.px = null;
        this.kick();
      };
      window.addEventListener('pointermove', this.onMove, { passive: true });
      document.addEventListener('mouseout', this.onLeave, { passive: true });
      window.addEventListener('scroll', this.kick, { passive: true });
      window.addEventListener('resize', this.kick, { passive: true });
      this.io = new IntersectionObserver((entries) => {
        this.visible = entries[entries.length - 1].isIntersecting;
        this.cd.classList.toggle('is-off', !this.visible);
        if (this.visible) this.kick();
      });
      this.io.observe(this);
      this.makeButton();
      this.setMode(PHONE.matches);
      this.onMode = () => { if (!this.leaving) this.setMode(PHONE.matches); };
      if (PHONE.addEventListener) PHONE.addEventListener('change', this.onMode);
      this.onShow = (e) => { if (e.persisted) this.restore(); };
      window.addEventListener('pageshow', this.onShow);
    }

    disconnectedCallback() {
      if (this.onMove) window.removeEventListener('pointermove', this.onMove);
      if (this.onLeave) document.removeEventListener('mouseout', this.onLeave);
      window.removeEventListener('scroll', this.kick);
      window.removeEventListener('resize', this.kick);
      if (this.io) this.io.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);
      if (this.onMode && PHONE.removeEventListener) PHONE.removeEventListener('change', this.onMode);
      if (this.onShow) window.removeEventListener('pageshow', this.onShow);
    }

    // 7.18: phones (below 768px): the disc always out and spinning, the case unwrapped (its <picture> source) and not a
    // button. Wider: the case is the button and the disc rests in, as before.
    setMode(phone) {
      const c = this.caseEl;
      this.phoneMode = phone;
      clearTimeout(this.outT);
      if (phone) {
        ['role', 'tabindex', 'aria-pressed', 'aria-label'].forEach((a) => c.removeAttribute(a));
        this.unwrap(false);
        this.out = true;
      } else {
        c.setAttribute('role', 'button');
        c.setAttribute('tabindex', '0');
        c.setAttribute('aria-pressed', 'false');
        c.setAttribute('aria-label', 'Take out the ' + (this.dataset.title || '') + ' CD');
        if (this.out) this.unwrap(false);
        this.out = false;
      }
      this.cd.classList.toggle('is-out', this.out);
      this.kick();
    }

    /* ---------- 7.18: the search bar's pack-up ---------- */
    // First use of the search: a phone fetches the wrapped photo (it never shows otherwise) and readies the unwrapped
    // photo as its own layer (the same file its <picture> already shows, so it comes from the cache).
    prepareLeave() {
      if (!this.live || this.prepared) return;
      this.prepared = true;
      if (!this.phoneMode || !this.phoneSrc || !this.wrapImg || !this.openImg) return;
      try {
        const im = new Image();
        im.decoding = 'async';
        im.sizes = this.wrapImg.getAttribute('sizes') || '';
        im.srcset = this.wrapImg.getAttribute('srcset') || '';
        im.src = this.wrapImg.getAttribute('src');
        this.wrapWarm = im;
      } catch (e) { /* only a head start */ }
      this.loadOpen();
    }

    // Phones: show the unwrapped photo on its own layer (no transition: the frame does not change), then give the
    // case <img> its wrapped photo back (drop the phone <source>), so the wrap can sweep back on. False when the
    // layer is not ready: the case then leaves unwrapped.
    toOpenLayer() {
      const o = this.openImg, src = this.phoneSrc, c = this.caseEl;
      if (!o || !src || !src.parentNode || !this.wrapImg) return false;
      if (!o.classList.contains('is-in') || !o.complete || !o.naturalWidth) return false;
      c.classList.add('is-snap', 'is-open');
      void c.offsetWidth;
      src.remove();
      void c.offsetWidth;
      c.classList.remove('is-snap');
      return true;
    }

    leave(go, topEdge) {
      if (!this.live || !this.cd.animate) { setTimeout(go, 0); return 0; }
      const r0 = this.cd.getBoundingClientRect();
      const top = Math.max(0, topEdge || 0);
      const seen = (Math.min(r0.bottom, innerHeight) - Math.max(r0.top, top)) / Math.max(1, r0.height);
      // the case is (mostly) scrolled away: only the page fades, then the page goes
      if (seen < 0.3) { setTimeout(go, 240); return 0; }
      const phone = this.phoneMode;
      const dir = r0.left + r0.width / 2 > innerWidth / 2 ? 1 : -1; // the nearer edge (a tie goes left), as on the hub
      this.leaving = true;
      this.hold = false;
      this.px = null;
      clearTimeout(this.outT);
      this.classList.remove('is-sliding', 'is-armed');
      this.classList.add('is-packing');
      const hadOut = this.out || this.x > 4;
      const rewrap = phone ? this.toOpenLayer() : !!this.openImg && this.caseEl.classList.contains('is-open');
      // 1. the disc back into the case (a stiffer spring than a press: about 0.25 s), spin off
      this.out = false;
      this.omega = phone ? 24 : 18;
      this.cd.classList.remove('is-out');
      this.caseEl.setAttribute('aria-pressed', 'false');
      this.kick();
      // 2. the wrap sweeps back on (0.3 s while packing; on a phone only once the wrapped photo is in)
      if (rewrap) {
        setTimeout(() => {
          if (!this.leaving) return;
          if (phone && !(this.wrapImg.complete && this.wrapImg.naturalWidth)) return;
          this.caseEl.classList.remove('is-open');
        }, 110);
      }
      // 3. the case slides off by the nearer edge; the page is asked for just before it is gone
      const slideAt = phone ? (rewrap ? 380 : hadOut ? 240 : 160) : (rewrap ? 450 : hadOut ? 300 : 180);
      setTimeout(() => {
        if (!this.leaving) return;
        try {
          const r = this.cd.getBoundingClientRect();
          const dx = Math.round(dir > 0 ? innerWidth - r.left + 60 : -(r.right + 60));
          const dur = phone ? 300 : clamp(Math.round(Math.abs(dx) * 0.55), 450, 700);
          this.exitAnim = this.cd.animate([
            { transform: 'translate3d(0, 0, 0) rotate(0deg) scale(1)' },
            { transform: 'translate3d(' + dx + 'px, -2%, 0) rotate(' + (dir * 8) + 'deg) scale(1.03)' }
          ], { duration: dur, easing: EXIT_EASE, fill: 'forwards' });
          setTimeout(go, dur - (phone ? 40 : 60));
        } catch (e) { go(); }
      }, slideAt);
      setTimeout(go, slideAt + 1100); // a safety net (a throttled timer)
      return dir;
    }

    // Back from the next page (back-forward cache): the CD as it was, at once.
    restore() {
      if (!this.leaving) return;
      this.leaving = false;
      this.omega = 0;
      if (this.exitAnim) { this.exitAnim.cancel(); this.exitAnim = null; }
      this.classList.remove('is-packing');
      const c = this.caseEl;
      c.classList.add('is-snap');
      if (this.phoneSrc && !this.phoneSrc.parentNode && this.wrapImg && this.wrapImg.parentNode) {
        this.wrapImg.parentNode.insertBefore(this.phoneSrc, this.wrapImg);
      }
      c.classList.remove('is-open');
      this.setMode(PHONE.matches);
      void c.offsetWidth;
      c.classList.remove('is-snap');
    }

    makeButton() {
      const c = this.caseEl; // role and labels: setMode (7.18: phones have none)
      const warm = () => { if (!this.phoneMode) this.loadOpen(); };
      ['pointerenter', 'pointerdown', 'focus'].forEach((t) => c.addEventListener(t, warm, { passive: true }));
      const toggle = () => {
        if (this.phoneMode || this.leaving) return; // 7.18: phones keep the disc out
        this.out = !this.out;
        c.setAttribute('aria-pressed', this.out ? 'true' : 'false');
        clearTimeout(this.outT);
        if (this.out && this.openImg) {
          // the wrap comes off first, then the disc follows (when the photo is ready)
          this.loadOpen();
          const ready = this.openImg.classList.contains('is-in');
          this.unwrap(true);
          if (ready) {
            this.outT = setTimeout(() => { if (this.out) { this.cd.classList.add('is-out'); this.kick(); } }, 250);
            return;
          }
        } else if (!this.out) {
          this.unwrap(false);
        }
        this.cd.classList.toggle('is-out', this.out);
        this.kick();
      };
      c.addEventListener('click', toggle);
      c.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
      });
    }

    // The unwrapped case photo (custom.cd_case_open): loaded on demand, shown while the disc is out.
    loadOpen() {
      const img = this.openImg;
      if (!img || !img.dataset.src) return;
      img.addEventListener('load', () => {
        img.classList.add('is-in');
        if (this.out && !this.phoneMode) this.unwrap(true);
      }, { once: true });
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
    }

    unwrap(on) {
      if (!this.openImg) return;
      this.caseEl.classList.toggle('is-open', on && this.openImg.classList.contains('is-in'));
    }

    loadDisc() {
      const img = this.disc.querySelector('img[data-src]');
      const caseImg = this.caseEl.querySelector('img.jk-cd__wrap') || this.caseEl.querySelector('img');
      if (!img) return;
      let started = false;
      const go = () => {
        if (started) return;
        started = true;
        img.addEventListener('load', () => this.disc.classList.add('is-in'), { once: true });
        img.srcset = img.dataset.srcset;
        img.src = img.dataset.src;
      };
      if (!caseImg || (caseImg.complete && caseImg.naturalWidth > 0)) { go(); return; }
      caseImg.addEventListener('load', go, { once: true });
      caseImg.addEventListener('error', go, { once: true });
      setTimeout(go, 3000);
    }

    kick() {
      if (!this.visible || this.raf) return;
      this.last = performance.now();
      this.raf = requestAnimationFrame(this.tick);
    }

    target() {
      const r = this.cd.getBoundingClientRect();
      const w = this.cd.offsetWidth;
      const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
      const rad = w * 0.315, edge = 8;
      // Sideways limits: out of the open (right) side only, up to half the case width,
      // less if the disc would leave the screen. No vertical travel (7.12).
      const axR = clamp(innerWidth - edge - rad - cx, 0, w * 0.5);
      const axL = 0;
      this.dataset.axl = axL.toFixed(1);
      this.dataset.axr = axR.toFixed(1);
      if (this.hold || this.leaving) { // (7.18: packing up puts the disc all the way in)
        this.tx = 0; this.ty = 0;
      } else if (this.out) {
        this.tx = Math.min(w * 0.45, axR); this.ty = 0;
      } else if (this.px !== null) {
        // Pull toward the pointer, sideways only: its horizontal offset from the case centre
        // as a share of the room between the case and the screen edge on the pointer's side.
        // The pointer's height plays no part (7.12).
        const ox = this.px - cx;
        const nx = clamp(ox / Math.max(w * 0.5, ox < 0 ? cx : innerWidth - cx), -1, 1);
        this.tx = Math.max(0, nx) * axR; this.ty = 0;
      } else {
        // Resting peek, tucking back into the case as the header scrolls off the top.
        const s = this.getBoundingClientRect();
        const p = clamp(-s.top / Math.max(1, s.height), 0, 1);
        this.tx = Math.min(w * 0.31, axR) * (1 - p); this.ty = 0;
      }
      return w;
    }

    tick(now) {
      let dt = Math.min(0.064, (now - this.last) / 1000);
      this.last = now;
      const w = this.target();
      // Critically damped spring, in steps of at most 1/120 s. Sideways only (7.12).
      while (dt > 0) {
        const h = Math.min(dt, 1 / 120);
        const om = this.omega || OMEGA; // 7.18: stiffer while packing up
        this.vx += (om * om * (this.tx - this.x) - 2 * om * this.vx) * h;
        this.x += this.vx * h;
        dt -= h;
      }
      this.y = 0; this.vy = 0;
      // Hard tether: never out of the hinge side, never past its travel, even mid-swing.
      const ax = +this.dataset.axr;
      if (this.x < 0) { this.x = 0; if (this.vx < 0) this.vx = 0; }
      if (this.x > ax) { this.x = ax; if (this.vx > 0) this.vx *= 0.5; }
      const roll = (this.x / (w * 0.5)) * 24;
      this.disc.style.transform = `translate3d(${this.x.toFixed(2)}px, 0px, 0) rotate(${roll.toFixed(2)}deg)`;
      this.caseEl.style.transform = `translate3d(${(-this.x * 0.03).toFixed(2)}px, 0, 0)`;
      const moving = Math.abs(this.tx - this.x) > 0.1 || Math.abs(this.vx) > 0.5;
      this.raf = this.visible && moving ? requestAnimationFrame(this.tick) : 0;
    }
  }
  customElements.define('jk-cd-cover', JkCdCover);

  class JkTracklist extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('data-picker')) return;
      this.btns = Array.from(this.querySelectorAll('button.jk-cdh__track[data-key]'));
      this.all = this.querySelector('.jk-cdh__all');
      const cardFor = (k) => document.querySelector('.jk-dc__card[data-key="' + CSS.escape(k) + '"]');
      this.btns.forEach((b) => b.addEventListener('click', () => { const c = cardFor(b.dataset.key); if (c) c.click(); }));
      if (this.all) {
        this.all.addEventListener('click', () => {
          const clear = document.querySelector('jk-design-cards .jk-dc__clear');
          if (clear) clear.click();
          else { const on = document.querySelector('.jk-dc__card[aria-pressed="true"]'); if (on) on.click(); }
          const first = this.btns[0];
          if (first) first.focus({ preventScroll: true });
        });
      }
      this.sync = () => {
        this.pending = 0;
        let picked = false;
        this.btns.forEach((b) => {
          const c = cardFor(b.dataset.key);
          const on = !!c && c.getAttribute('aria-pressed') === 'true';
          if (on) picked = true;
          if (b.getAttribute('aria-pressed') !== String(on)) b.setAttribute('aria-pressed', String(on));
        });
        this.classList.toggle('is-picked', picked);
        if (this.all) this.all.hidden = !picked;
      };
      this.sync();
      this.mo = new MutationObserver(() => { if (!this.pending) this.pending = requestAnimationFrame(this.sync); });
      this.mo.observe(document.getElementById('ProductGridContainer') || document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-pressed'] });
    }

    disconnectedCallback() {
      if (this.mo) this.mo.disconnect();
    }
  }
  customElements.define('jk-tracklist', JkTracklist);
})();
